import type { AppSnapshot } from '../app/state';
import type { NodeMap } from '../lib/types';
import { getStoreState } from './index';
import { BOOT_ORDER, type BootPhase } from './slices/boot';
import type { PositionMap } from './slices/positions';

/**
 * The one place vanilla state is copied into the store.
 *
 * ONE WRITE POINT, ON PURPOSE. `app/state.ts`'s `publish()` is the single moment anything
 * learns the state moved — it is what `render()` calls, from ~50 sites, after every mutation,
 * including paths nobody remembers to notify by hand. Mirroring anywhere else would be a
 * second, partial answer to the same question, and the two would drift.
 *
 * WHY TWO REPRESENTATIONS AT ALL. The snapshot is a flat cached object for ordinary React
 * reads; the store is `subscribeWithSelector` Zustand, which is what lets a `MapNode` watch
 * ONE id and a `pointermove` handler read and commit without hooks at all. Neither can do the
 * other's job, so this copies once per publish rather than making every consumer choose.
 *
 * WHY THE IMPORT GOES THIS WAY ROUND. `app/state.ts` imports this module and calls it; this
 * module imports the store and only a TYPE from `app/state.ts`. So there is no runtime cycle —
 * the type import is erased — and the state stays the thing that knows about the store rather
 * than the store reaching for a snapshot whenever it feels like it.
 *
 * WHAT IS DELIBERATELY NOT MIRRORED, and it matters that the next stage knows:
 *
 *  - **The camera.** `view {x,y,k}` is the STORE's, written by the map's pan and zoom paths
 *    directly. It is not on the snapshot and must not be: a copy would be correct only at the
 *    moments something else happened to publish, and a value that is silently stale is worse
 *    than no copy at all. `saveViewState()` reaches it through `mapGetCamera()`, which is
 *    named at both ends for exactly that reason.
 *  - **Sizes.** Measured by `MapStage`'s `ResizeObserver` straight into the store. Nothing
 *    outside the map has any business knowing how tall a node happens to be.
 */

/**
 * Node positions, extracted from the node objects.
 *
 * `?? 0` rather than a throw for a missing coordinate: the server may hand back nulls for a
 * node that has never been laid out, and the layout pass assigns real numbers to every node
 * before anything reads them. Inventing a zero here matches what the arithmetic already does
 * with an absent value, and does not hide a bug that would otherwise surface.
 */
function positionsFrom(nodes: NodeMap): PositionMap {
  const out: PositionMap = {};
  for (const id in nodes) {
    const n = nodes[id];
    out[id] = { x: n.x ?? 0, y: n.y ?? 0, side: n.side ?? 0, depth: n.depth ?? 0 };
  }
  return out;
}

/**
 * Bring the store's boot phase up to the one the snapshot carries.
 *
 * ONE STEP AT A TIME, EVEN WHEN CATCHING UP, so `enterBootPhase`'s ordering guard stays the
 * only way the phase ever moves — which is what makes it worth having.
 *
 * `app/boot.ts` drives the phase directly now, so in the ordinary case this has nothing left to
 * do. It stays because the snapshot carries the phase, and a mirror that ignored one field
 * would be a mirror somebody has to remember the exception for.
 *
 * The single legal backwards move is a full reset to `booting`, which is a second sign-in on
 * the same browser starting over — `enterApp()` clears its own view-write latch at exactly that
 * moment, for exactly that reason.
 */
function syncBootPhase(announced: BootPhase): void {
  const st = getStoreState();
  const want = BOOT_ORDER.indexOf(announced);
  if (want < 0) return;
  if (announced === 'booting') {
    st.resetBoot();
    return;
  }
  const have = BOOT_ORDER.indexOf(st.bootPhase);
  if (want < have) {
    // Not a reset and not a step: boot has gone backwards without starting over, which means
    // an announcement was missed. Loud, because the view-write guard is derived from this and
    // a wrong phase means view state persisted at the wrong moment.
    console.error(`[boot] vanilla announced ${announced} while the store is at ${st.bootPhase}`);
    return;
  }
  let guard = BOOT_ORDER.length;
  while (BOOT_ORDER.indexOf(getStoreState().bootPhase) < want && guard-- > 0) {
    const next = BOOT_ORDER[BOOT_ORDER.indexOf(getStoreState().bootPhase) + 1];
    if (!getStoreState().enterBootPhase(next)) break;
  }
}

/**
 * Copy one snapshot into the store.
 *
 * Cheap by construction. The graph containers are ADOPTED rather than compared — the app
 * mutates the objects inside them in place, so container identity is the only thing that
 * carries "this changed", and `app/state.ts` replaces them on every publish for that reason
 * (see `slices/graph.ts`). Everything else is either a primitive or, in the case of positions,
 * a value comparison that preserves identity for whatever did not move.
 */
export function mirrorAppSnapshot(s: AppSnapshot): void {
  const st = getStoreState();

  st.replaceGraph({
    nodes: s.nodes,
    edges: s.edges,
    tags: s.tags,
    tagEdges: s.tagEdges,
    links: s.links,
    mapFiltered: s.mapFiltered,
  });
  st.setPositions(positionsFrom(s.nodes));

  st.setSession(s.currentUser, s.perm);
  st.setCapabilities(s.caps);
  st.setCurrentLocale(s.currentLocale);
  st.setUserDirectory(s.userDirectory, s.userDirectoryLoaded);
  st.setSessionChecked(s.booted);

  st.setEditing(s.editing);
  st.setLight(s.isLight);
  st.setSidebarOpen(s.sidebarOpen);
  st.setShowLinks(s.showLinks);
  st.setMinimapOn(s.minimapOn);
  st.setMinimapBright(s.minimapBright);
  st.setLinkArmed(s.linkArmed);
  st.setCtrlAdd(s.ctrlAdd);
  st.setAiDocMode(s.aiDocMode);
  st.setHeatmapOn(s.heatmapOn);
  st.setDocsPinned(s.docsPinned);
  st.setUndoLabel(s.undoLabel);
  st.setAdminScreen(s.adminScreen);
  st.setTopbarOverflow(s.topbarOverflow);

  st.setActiveTags(s.activeTags);
  // `searchQuery` is DERIVED here rather than copied, even though the snapshot carries both.
  // `app/state.ts` keeps the same invariant — `applySearch()` is the only writer of
  // `searchQuery` and it computes exactly `searchText.trim().toLowerCase()`, and the view
  // restore assigns an already-normalised string to both — so copying it would introduce a
  // second way for the pair to be set, which is how they would eventually disagree.
  st.setSearchText(s.searchText);
  st.setSearchScope(s.searchScope);
  st.setSearchDocKind(s.searchDocKind);
  st.setSearchContentHits(s.searchContentHits);
  st.setForceOpen(s.forceOpen);
  // '' is the vanilla "no filter" value; the store's is null, and letting the empty string
  // through would make `nodeHasActivePipelineStage` compare against it.
  st.setActiveDocStatus(s.activeDocStatus || null);
  st.setChatHighlight(s.chatHighlight);
  st.setAssignHighlight(s.assignHighlight);

  st.setDocCatalogue({
    docTitles: s.docTitles,
    docUrls: s.docUrls,
    docTypes: s.docTypes,
    docAiGenerated: s.docAiGenerated,
  });
  st.setDocWorkflow({ docWorkflow: s.docWorkflow, docWorkflowLoaded: s.docWorkflowLoaded });
  st.setMapHealth({ mapHealth: s.mapHealth.byNode, mapHealthLoaded: s.mapHealth.loaded });
  st.setProjectProgress(s.projectProgress);
  st.setDocView({
    docOpen: s.docOpen,
    docCurrentId: s.docCurrentId,
    docCurrent: s.docCurrent,
    docCurrentTitle: s.docCurrentTitle,
    docNavNodeId: s.docNavNodeId,
    docOpenSeq: s.docOpenSeq,
    docEditSessionId: s.docEditSessionId,
    docRevision: s.docRevision,
  });

  st.setPresenceRoster(s.presenceRoster);

  // Last, so that anything watching the phase sees a store that already holds the state that
  // phase is asserting about. A subscriber woken by `ready` must not find an empty graph.
  syncBootPhase(s.bootPhase);
}
